import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AnimateButton from 'ui-component/extended/AnimateButton';
import getAxiosInstance from 'api';
import NotificationSnackBar from 'common/NotificationSnackBar';
import toast from 'react-hot-toast';

const CreatePassword = () => {
  const theme = useTheme();
  const {id} = useParams();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({
    password: '',
    confirmPassword: '',
    passwordError: '',
    confirmPasswordError: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({
    alertMessage: '',
    isOpen: false,
    status: '',
  });

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const handleClose = () => {
    setAlert((prev) => ({ ...prev, isOpen: false }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
      [`${name}Error`]: '',
    }));
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    const {  password, confirmPassword } = credentials;
    let hasError = false;
    const updated = { ...credentials };
    if (!password) {
      updated.passwordError = 'Password is required';
      hasError = true;
    }
    if (!confirmPassword) {
      updated.confirmPasswordError = 'Confirm Password is required';
      hasError = true;
    } else if (confirmPassword !== password) {
      updated.confirmPasswordError = 'Passwords do not match';
      hasError = true;
    }

    if (hasError) {
      setCredentials(updated);
      return;
    }

    try {
      const res = await getAxiosInstance(
        'businessPartner/resetPassword',
        'post',
        { newpassword: password, confirmpassword: confirmPassword, otp:id },
        { 'journey-type': 'ChangePassword' }
      );

      if (res?.data?.codes === 200) {
        toast.success('Password changed successfully');
        setTimeout(() => {
            navigate('/login');
        })
      } else {
        toast.error(res?.data?.message || 'Failed to change password');
      }
    } catch (err) {
        toast.error(err?.message || 'Something went wrong');
    }
  };

  return (
    <>
      <Grid container component="form" spacing={3} onSubmit={handleSubmitClick}>

        <Grid item xs={12}>
          <InputLabel>Password</InputLabel>
          <OutlinedInput
            fullWidth
            name="password"
            type={ "text"}
            value={credentials.password}
            onChange={handleChange}
            placeholder="Enter new password"
            error={!!credentials.passwordError}
          />
          {credentials.passwordError && (
            <Typography color="error" fontSize={12}>
              {credentials.passwordError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <InputLabel>Confirm Password</InputLabel>
          <OutlinedInput
            fullWidth
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={credentials.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm new password"
            error={!!credentials.confirmPasswordError}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
          {credentials.confirmPasswordError && (
            <Typography color="error" fontSize={12}>
              {credentials.confirmPasswordError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <AnimateButton>
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              color="secondary"
            >
              Change Password
            </Button>
          </AnimateButton>
        </Grid>
      </Grid>

      <NotificationSnackBar alert={alert} handleClose={handleClose} />
    </>
  );
};

export default CreatePassword;
